'use client'
import { useState } from 'react'
import Link from 'next/link'
import { useLanguage } from '@/lib/context/LanguageContext'
import SectionHeader from '@/components/ui/SectionHeader'
import AddToCart from '@/components/cart/AddToCart'
import { formatToman } from '@/lib/catalog/api'

// TEMP (revert this week): aesthetic products → medical supplies.
// Order mirrors t.products.items: mask, gown, gloves.
const CARD_IMG = [
  '/products/mask.png',
  '/products/gown.png',
  '/products/gloves.jpeg',
]

// Catalog slugs, keyed by position in t.products.items — the DB-backed detail
// page and the cart are both addressed by slug. Keep in sync with db.seed_products.
const CARD_SLUGS = ['mask', 'gown', 'gloves']

/**
 * Product card — two layers:
 *
 *   1. A flip *tile* (vial front ↔ description back). It's a toggle, not a
 *      link, so it never nests interactive controls. Hover flips on pointer
 *      devices; tap/Enter flips on touch + keyboard. The hidden face is
 *      aria-hidden so screen readers read one side at a time.
 *   2. A persistent caption below it — name (heading), type, and the CTA —
 *      always visible and scannable. The card's identity and the call to
 *      action never depend on the flip; the back face is a *bonus* detail.
 *
 * This keeps the front clean (just the product) while fixing the earlier
 * version, where the vial-only card carried no readable label and hid both
 * the product type and the CTA behind a hover/tap.
 */
function ProductCard({
  name,
  tagline,
  description,
  cta,
  img,
  href,
  slug,
  price,
  index,
  isRTL,
}: {
  name: string
  tagline: string
  description: string
  cta: string
  img: string
  href: string
  slug: string
  /** Live catalog price in toman; undefined/null (API down, unknown slug) hides the line. */
  price?: number | null
  index: number
  isRTL: boolean
}) {
  const [flipped, setFlipped] = useState(false)
  const arrow = isRTL ? '←' : '→'

  const cta_el = href.startsWith('/') ? (
    <Link href={href} className="pc-cta">
      {cta}
      <span aria-hidden>{arrow}</span>
    </Link>
  ) : (
    <a href={href} className="pc-cta">
      {cta}
      <span aria-hidden>{arrow}</span>
    </a>
  )

  return (
    <div
      className="pc"
      style={{ animationDelay: `${index * 90}ms` }}
    >
      {/* ---- Flip tile: a toggle, no link inside ---- */}
      <div
        className={'flip' + (flipped ? ' is-flipped' : '')}
        role="button"
        tabIndex={0}
        aria-pressed={flipped}
        aria-label={`${name} — ${flipped ? 'hide' : 'show'} details`}
        onClick={() => setFlipped((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setFlipped((v) => !v)
          }
        }}
      >
        <div className="flip-inner">
          {/* Front — just the vial */}
          <div className="flip-face flip-front" aria-hidden={flipped}>
            <img
              src={img}
              alt={`${name} — ${tagline}`}
              className="ff-img"
              loading="lazy"
              decoding="async"
            />
          </div>
          {/* Back — the fuller description, a bonus detail */}
          <div className="flip-face flip-back" aria-hidden={!flipped}>
            <p className="fb-desc">{description}</p>
          </div>
        </div>
      </div>

      {/* ---- Persistent identity + CTA ---- */}
      <div className="pc-meta">
        <h3 className="pc-name">{name}</h3>
        <p className="pc-type">{tagline}</p>
        {typeof price === 'number' && <p className="pc-price">{formatToman(price)}</p>}
        {cta_el}
        <div className="mt-4">
          <AddToCart slug={slug} />
        </div>
      </div>

      <style>{`
        @keyframes card-rise {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .pc {
          opacity: 0;
          animation: card-rise 480ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        /* ---- Flip tile ---- */
        .flip {
          position: relative;
          aspect-ratio: 5 / 7;
          perspective: 1600px;
          cursor: pointer;
          outline: none;
        }
        .flip-inner {
          position: absolute;
          inset: 0;
          transform-style: preserve-3d;
          transition: transform 750ms cubic-bezier(0.22, 1, 0.36, 1);
          will-change: transform;
        }
        /* Flip is driven only by the tap/keyboard toggle (.is-flipped) and by
           real hover — never by :focus-visible, so a focused keyboard user can
           still toggle back (a focus-held flip would trap it open). */
        .flip.is-flipped .flip-inner { transform: rotateY(180deg); }
        html[dir='rtl'] .flip.is-flipped .flip-inner { transform: rotateY(-180deg); }
        @media (hover: hover) {
          .flip:hover .flip-inner { transform: rotateY(180deg); }
          html[dir='rtl'] .flip:hover .flip-inner { transform: rotateY(-180deg); }
        }

        .flip-face {
          position: absolute;
          inset: 0;
          -webkit-backface-visibility: hidden;
          backface-visibility: hidden;
          border-radius: var(--radius-card, 14px);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .flip-front {
          border: 1px solid var(--color-line);
          background:
            radial-gradient(ellipse 72% 55% at 50% 56%, rgba(222,188,158,0.45), transparent 72%),
            var(--color-surface-alt, #f1e9dd);
          padding: 1.4rem 1.1rem;
          justify-content: center;
          container-type: size;
          box-shadow:
            0 24px 50px -24px rgba(60, 30, 24, 0.34),
            0 6px 16px -10px rgba(60, 30, 24, 0.2);
        }
        .ff-img {
          max-height: 84cqh;
          max-width: 90%;
          width: auto;
          margin: 0 auto;
          object-fit: contain;
          filter: drop-shadow(0 18px 26px rgba(60, 30, 24, 0.16));
          transition: transform 750ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        @media (hover: hover) {
          .flip:hover .ff-img { transform: scale(1.04); }
        }
        /* Back — description as a bonus detail layer (soft maroon halo). */
        .flip-back {
          transform: rotateY(180deg);
          background: var(--color-surface, #f6efe2);
          color: var(--color-ink, #1a1a17);
          box-shadow: 0 0 26px 6px color-mix(in oklab, var(--color-accent, #801424) 42%, transparent);
          padding: 1.9rem 1.7rem;
          justify-content: center;
          text-align: start;
        }
        .fb-desc {
          font-size: 0.92rem;
          line-height: 1.65;
          color: var(--color-ink-muted, #5a5852);
          white-space: pre-line;
        }

        /* ---- Persistent caption ---- */
        .pc-meta {
          margin-top: 1.1rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
        .pc-name {
          font-family: var(--font-display-serif, Georgia, serif);
          font-size: clamp(1.45rem, 1.6vw, 1.85rem);
          line-height: 1.05;
          color: var(--color-ink);
          font-variation-settings: "opsz" 40, "wght" 640;
        }
        .pc-type {
          margin-top: 0.35rem;
          max-width: 24ch;
          font-size: 0.82rem;
          line-height: 1.35;
          color: var(--color-accent);
        }
        .pc-price {
          margin-top: 0.55rem;
          font-size: 0.95rem;
          font-weight: 700;
          color: var(--color-accent);
          font-variant-numeric: tabular-nums;
        }
        .pc-cta {
          margin-top: 0.9rem;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          font-size: 0.9rem;
          font-weight: 700;
          color: var(--color-ink);
          text-decoration: none;
          border-bottom: 1px solid color-mix(in oklab, var(--color-ink) 35%, transparent);
          padding-bottom: 2px;
          transition: border-color 200ms ease;
        }
        .pc-cta:hover { border-color: var(--color-accent); }

        .flip:focus-visible .flip-front,
        .flip:focus-visible .flip-back {
          outline: 2px solid var(--color-blue);
          outline-offset: 3px;
        }

        @media (prefers-reduced-motion: reduce) {
          .pc { animation: none; opacity: 1; }
          .flip-inner { transition: none; }
          .ff-img { transition: none; }
        }
      `}</style>
    </div>
  )
}

export default function Products({
  prices,
}: {
  /** slug → price_toman from the live catalog (see getHomePriceMap). */
  prices?: Record<string, number | null>
}) {
  const { t, isRTL } = useLanguage()

  return (
    <section
      id="products"
      className="relative bg-surface pt-16 pb-24 sm:pt-20 sm:pb-32"
      style={{ direction: isRTL ? 'rtl' : 'ltr' }}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeader rule heading={t.products.heading} sub={t.products.sub} />

        <div className="mx-auto mt-14 grid max-w-5xl gap-x-10 gap-y-12 sm:grid-cols-2 md:grid-cols-3">
          {t.products.items.map((item, i) => (
            <ProductCard
              key={item.name}
              name={item.name}
              tagline={item.tagline}
              description={item.description}
              cta={t.products.viewProduct}
              img={CARD_IMG[i]}
              href={`/products/${CARD_SLUGS[i]}`}
              slug={CARD_SLUGS[i]}
              price={prices?.[CARD_SLUGS[i]]}
              index={i}
              isRTL={isRTL}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
